import React, { useEffect, useState } from 'react';
import { Map, MapkitProvider, useMap } from 'react-mapkit';
import mapkitToken from '../../helpers/mapkit-token';
import usePrevious from '../../services/hooks/usePrevious';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import { RouteFormatter } from '../../routes';
import { showPriceCredits } from '../../helpers/price';
import emptySpotImg from '../../assets/img/empty-spot.png';
import starImg from '../../assets/img/star-solid.png';
import './style.scss';

const SIZE_UNITS = {
    SQ_FT: 'sq ft',
    ACRES: 'acres',
};

const showSize = (size, sizeUnits) => {
    const spotSize = parseFloat(size);
    if (SIZE_UNITS[sizeUnits] === SIZE_UNITS.ACRES) {
        if (spotSize < 1) {
            return `${spotSize} acres`;
        }
        if (spotSize == 1) {
            return '1 acre';
        }
        if (spotSize > 1 && spotSize < 10) {
            return `${Math.round(spotSize * 10) / 10} acres`;
        }
        if (spotSize >= 10) {
            return `${parseInt(spotSize)} acres`;
        }
    } else {
        return `${spotSize} ${SIZE_UNITS[sizeUnits] ? SIZE_UNITS[sizeUnits] : 'acres'}`;
    }
};

const SPOT_ENCLOSURE = {
    FULLY_FENCED: 'Fully Fenced',
    PARTIALLY_FENCED: 'Partially Fenced',
    UNFENCED: 'Unfenced',
};

const customAnno = (_, options) => {
    const spot = options.data;
    const spotSize = showSize(spot.size, spot.sizeUnits) || '';
    const isActive = spot.id == spot.activeSpotId;

    const div = document.createElement('div');

    const span = document.createElement('span');
    span.innerText = spot?.publicPark ? 'Public park' : spotSize;

    div.className = `sniff-listings-anno ${!spot?.publicPark && !spotSize ? 'no-size' : ''} ${isActive ? 'active' : ''}`;

    const spanSVG = document.createElement('span');
    spanSVG.className = 'sniff-listings-anno_span-svg';

    const topSVG = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    topSVG.setAttribute('width', 14);
    topSVG.setAttribute('height', 14);
    topSVG.setAttribute('viewBox', '0 0 20 20');
    topSVG.setAttribute('fill', 'none');

    const svgP1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const svgP2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    svgP1.setAttribute(
        'd',
        'M15 20a.623.623 0 0 1-.53-.292l-3.052-4.844a7.477 7.477 0 0 1-2.836 0l-3.054 4.844a.621.621 0 0 1-.6.288.628.628 0 0 1-.52-.423l-1.136-3.406-2.77-.555a.628.628 0 0 1-.465-.401.63.63 0 0 1 .104-.607l3.122-3.815A7.507 7.507 0 0 1 2.5 7.5C2.5 3.364 5.864 0 10 0s7.5 3.364 7.5 7.5c0 1.137-.263 2.27-.762 3.29l3.121 3.814a.626.626 0 0 1-.361 1.009l-2.77.554-1.135 3.405A.626.626 0 0 1 15 20Zm-.175-2.075.833-2.498a.624.624 0 0 1 .47-.415l2.105-.42-2.183-2.667a7.552 7.552 0 0 1-3.38 2.583l2.155 3.417ZM3.873 15.012c.219.044.4.204.47.415l.832 2.497 2.153-3.417a7.553 7.553 0 0 1-3.38-2.583l-2.182 2.667 2.107.421ZM10 1.25A6.257 6.257 0 0 0 3.75 7.5 6.258 6.258 0 0 0 10 13.75a6.258 6.258 0 0 0 6.25-6.25A6.258 6.258 0 0 0 10 1.25Z'
    );
    svgP2.setAttribute(
        'd',
        'M7.577 11.433a1.105 1.105 0 0 1-.967-.555 1.123 1.123 0 0 1-.064-.991l.734-1.685-1.322-1.3c-.21-.205-.33-.481-.335-.778a1.102 1.102 0 0 1 .313-.79c.21-.218.492-.338.79-.338h1.408l.867-1.704a1.129 1.129 0 0 1 1.524-.478c.2.106.363.268.47.47L11.864 5h1.412c.591 0 1.085.486 1.1 1.083.007.31-.116.61-.339.825l-1.317 1.294.73 1.676a1.12 1.12 0 0 1-.594 1.469 1.118 1.118 0 0 1-.992-.064L10 10.232l-1.873 1.054a1.105 1.105 0 0 1-.55.146ZM10 8.891c.107 0 .213.027.307.08l1.836 1.033-.739-1.698a.628.628 0 0 1 .135-.695l1.385-1.361h-1.441a.623.623 0 0 1-.558-.342L10 4.086l-.926 1.819a.622.622 0 0 1-.556.342H7.075L8.462 7.61c.183.18.237.459.135.695l-.74 1.698 1.837-1.033A.62.62 0 0 1 10 8.89Z'
    );
    svgP1.setAttribute('fill', isActive ? '#fff' : '#3AA648');
    svgP2.setAttribute('fill', isActive ? '#fff' : '#3AA648');
    topSVG.appendChild(svgP1);
    topSVG.appendChild(svgP2);
    spanSVG.appendChild(topSVG);

    (spot.size || spot.publicPark) && div.appendChild(span);

    const padding = document.createElement('span');
    padding.setAttribute('style', 'width: 6px;');

    spot.size && spot.top && div.appendChild(padding);
    spot.top && div.appendChild(spanSVG);

    return div;
};

const customAnnoMyLoc = () => {
    const div = document.createElement('div');
    div.className = 'sniff-listings-anno-my-loc';
    div.appendChild(document.createElement('div'));
    return div;
};

const calloutDelegate = {
    calloutElementForAnnotation: (anno) => {
        const spot = anno.data;
        const { top, bottom, left, right } = spot.calloutPosition;

        const elementCont = document.createElement('div');
        elementCont.className = `map-spot-callout-cont ${top ? 'call-top' : ''} ${bottom ? 'call-bottom' : ''} ${left ? 'call-left' : ''} ${
            right ? 'call-right' : ''
        }`;

        const element = elementCont.appendChild(document.createElement('div'));
        element.className = 'map-spot-callout';

        const imgDiv = element.appendChild(document.createElement('div'));
        imgDiv.className = 'map-spot-callout__imgDiv';
        const img = imgDiv.appendChild(document.createElement('img'));
        const photo = (spot.spotPhotos && spot.spotPhotos[0].imageAws) || (spot.allPhotos && spot.allPhotos[0]) || emptySpotImg;
        img.src = ImageHelper.getUrlFromOptions(photo, `fit-in/${IMG_RESIZES.x0256}`);
        img.alt = spot.title;
        img.width = '234';
        img.height = '150';
        img.loading = 'lazy';

        if (!anno.data.listings) {
            const spotType = element.appendChild(document.createElement('p'));
            spotType.className = 'spot-type-public';
            spotType.innerText = spot.publicPark ? 'Public dog park' : 'Private dog park';
        }

        const title = element.appendChild(document.createElement('h3'));
        title.textContent = spot.title;

        if (spot.enclosureType || spot.size) {
            const detail = element.appendChild(document.createElement('div'));
            detail.className = 'map-spot-callout__detail';
            detail.textContent = `${spot.enclosureType ? SPOT_ENCLOSURE[spot.enclosureType.toUpperCase()] : ''}${
                spot.size ? ' — ' + showSize(spot.size, spot.sizeUnits) : ''
            }`;
        }

        const price = element.appendChild(document.createElement('div'));
        price.className = 'map-spot-callout__price';
        if (spot.price) {
            const priceNumber = price.appendChild(document.createElement('span'));
            priceNumber.className = 'snif-p snif-semibold';
            priceNumber.textContent = showPriceCredits(spot.price);
        }
        const priceDesc = price.appendChild(document.createElement('span'));
        priceDesc.textContent = spot.price ? ' dog / hour' : 'No fee info';

        const rating = element.appendChild(document.createElement('div'));
        rating.className = 'map-spot-callout__rating';

        if (spot.rating) {
            const fixedRating = Math.round(spot.rating * 10) / 10;
            const star = rating.appendChild(document.createElement('img'));
            star.src = starImg;
            star.alt = 'Star';
            star.width = '16';
            star.height = '16';
            star.loading = 'lazy';
            const ratingNumber = rating.appendChild(document.createElement('span'));
            ratingNumber.className = 'text-dark';
            ratingNumber.textContent = `${fixedRating} —`;
            const reviews = rating.appendChild(document.createElement('span'));
            reviews.textContent = `(${spot.reviewsCount})`;
        } else if (spot.new) {
            const ratingNew = rating.appendChild(document.createElement('p'));
            ratingNew.textContent = 'New!';
        }
        element.onclick = (e) => spot.handleClick(e);
        return elementCont;
    },
    calloutAnchorOffsetForAnnotation: () => new DOMPoint(0, 0),
};

const getBoundRect = (map) => {
    const mapBoundRect = map.region.toBoundingRegion();
    const data = {
        ne: {
            lat: mapBoundRect.northLatitude,
            lng: mapBoundRect.eastLongitude,
        },
        sw: {
            lat: mapBoundRect.southLatitude,
            lng: mapBoundRect.westLongitude,
        },
    };
    return data;
};

const getCenter = (map) => {
    const latlng = map.center;
    const data = {
        lat: latlng.latitude,
        lng: latlng.longitude,
    };
    return data;
};

const RenderMap = ({ center, cameraDistance, spots, activeSpotId, setMapRect, recalcZoom, listings }) => {
    const prevCenter = usePrevious(center);
    const [hasZoomed, setHasZoomed] = useState(false);
    const [annotationsReady, setAnnotationsReady] = useState(false);

    const { map, mapProps, mapkit } = useMap({
        center: [center.latitude, center.longitude],
        showsCompass: 'hidden',
        cameraDistance,
    });

    const handleClickAnno = (e, spot) => {
        e?.preventDefault();
        e?.stopPropagation();
        const spotUrl = RouteFormatter.listings({ first: spot.spotUrl });
        window.open(spotUrl, '_blank');
    };

    useEffect(() => {
        if (map && mapkit) {
            setTimeout(() => {
                map.addEventListener('zoom-end', () => setMapRect(getBoundRect(map)));
                map.addEventListener('scroll-end', () => setMapRect(getBoundRect(map)));
            }, 2000);
        }
    }, [map, mapkit]);

    useEffect(() => {
        if (map && map.center && mapkit) {
            navigator.permissions.query({ name: 'geolocation' }).then(async (e) => {
                if (e.state === 'granted') {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const coords = new mapkit.Coordinate(position.coords.latitude, position.coords.longitude);
                            const annotation = new mapkit.Annotation(coords, customAnnoMyLoc);
                            map.addAnnotation(annotation);
                        },
                        (err) => console.warn(err),
                        { enableHighAccuracy: true, maximumAge: 60 * 1000, timeout: 30000 }
                    );
                }
            });
        }
    }, [map, mapkit]);

    useEffect(() => {
        if (map && mapkit && center?.longitude && center?.latitude) {
            if (prevCenter.latitude !== center.latitude || prevCenter.longitude !== center.longitude) {
                map.setCenterAnimated(new mapkit.Coordinate(center.latitude, center.longitude), false);
                map.setCameraDistanceAnimated(cameraDistance, false);
            }
        }
    }, [map, mapkit, prevCenter, center]);

    // Add annotations and then flag them as ready
    useEffect(() => {
        if (map && mapkit) {
            map.removeAnnotations(map.annotations);
            setAnnotationsReady(false); // reset first

            if (spots.length > 0) {
                const newMarkers = [];
                for (let i = spots.length; i > 0; i--) {
                    const spot = spots[i - 1];
                    const isActive = spot.id == activeSpotId;
                    const mapCenter = getCenter(map);
                    const calloutPosition = {
                        top: parseFloat(spot.latitude) > mapCenter.lat,
                        bottom: parseFloat(spot.latitude) <= mapCenter.lat,
                        right: parseFloat(spot.longitude) > mapCenter.lng,
                        left: parseFloat(spot.longitude) <= mapCenter.lng,
                    };

                    const options = {
                        callout: calloutDelegate,
                        data: {
                            ...spot,
                            activeSpotId,
                            calloutPosition,
                            handleClick: (e) => handleClickAnno(e, spot),
                            listings,
                        },
                        selected: isActive,
                        calloutEnabled: !isActive,
                    };

                    const coordinate = new mapkit.Coordinate(parseFloat(spot.latitude || 0), parseFloat(spot.longitude || 0));
                    const annotation = new mapkit.Annotation(coordinate, customAnno, options);
                    newMarkers.push(annotation);
                }
                map.addAnnotations(newMarkers);
                setAnnotationsReady(true);
            }
        }
    }, [map, mapkit, activeSpotId, spots]);

    useEffect(() => {
        if (map && mapkit && annotationsReady && spots?.length > 1 && (!hasZoomed || recalcZoom)) {
            let minLat = Infinity,
                maxLat = -Infinity,
                minLng = Infinity,
                maxLng = -Infinity;

            for (const spot of spots) {
                const lat = parseFloat(spot.latitude);
                const lng = parseFloat(spot.longitude);

                if (lat < minLat) {
                    minLat = lat;
                }
                if (lat > maxLat) {
                    maxLat = lat;
                }
                if (lng < minLng) {
                    minLng = lng;
                }
                if (lng > maxLng) {
                    maxLng = lng;
                }
            }

            const latSpan = (0.05 + (maxLat - minLat)) * 1.35;
            const lngSpan = (0.05 + (maxLng - minLng)) * 1.35;

            const span = new mapkit.CoordinateSpan(latSpan, lngSpan);
            const region = new mapkit.CoordinateRegion(map.center, span);

            setTimeout(() => {
                map.setRegionAnimated(region);
            }, 700);
            setHasZoomed(true);
        }
    }, [map, mapkit, annotationsReady, spots, hasZoomed]);

    return <Map {...mapProps} />;
};

const SniffAppleMapsRescue = (props) => {
    return (
        <MapkitProvider tokenOrCallback={mapkitToken} showsZoomControl>
            <RenderMap {...props} />
        </MapkitProvider>
    );
};

export default SniffAppleMapsRescue;
